/**
 * Browser speech for the Lab: feature detection, language mapping and voice
 * choice. Plain functions over the browser's Web Speech API; every function
 * takes the window (or synthesis object) so a test can stub it. Nothing is
 * stored or logged: spoken and dictated text only passes through the browser.
 */

export type SpeechLanguage = "he" | "en";
export type SpeechTag = "he-IL" | "en-US";

export const VOICE_INPUT_UNAVAILABLE = "Voice input needs Chrome or Edge";

/** The part of the Web Speech recognition API this build uses. */
export interface RecognitionResultLike {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}
export interface RecognitionEventLike {
  readonly resultIndex: number;
  readonly results: ArrayLike<RecognitionResultLike>;
}
export interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
export type RecognitionCtor = new () => RecognitionLike;

type SpeechWindow = {
  SpeechRecognition?: RecognitionCtor;
  webkitSpeechRecognition?: RecognitionCtor;
  speechSynthesis?: SynthesisLike;
  SpeechSynthesisUtterance?: UtteranceCtor;
};

export interface VoiceLike {
  readonly lang: string;
  readonly name: string;
}
export interface UtteranceLike {
  lang: string;
  voice: unknown;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}
export type UtteranceCtor = new (text: string) => UtteranceLike;
export interface SynthesisLike {
  speak(utterance: never): void;
  cancel(): void;
  getVoices(): VoiceLike[];
  addEventListener?(type: "voiceschanged", listener: () => void): void;
  removeEventListener?(type: "voiceschanged", listener: () => void): void;
}

function speechWindow(win?: unknown): SpeechWindow | null {
  const source = win ?? (typeof window === "undefined" ? null : window);
  return source === null ? null : (source as SpeechWindow);
}

export function recognitionCtor(win?: unknown): RecognitionCtor | null {
  const w = speechWindow(win);
  return w?.SpeechRecognition ?? w?.webkitSpeechRecognition ?? null;
}

export function synthesisOf(win?: unknown): SynthesisLike | null {
  const w = speechWindow(win);
  return w?.speechSynthesis != null && w.SpeechSynthesisUtterance != null
    ? w.speechSynthesis
    : null;
}

/** A session language (`he`, `en`, or a full tag) as the tag speech uses. */
export function speechTag(language: string | null | undefined): SpeechTag {
  return language?.toLowerCase().startsWith("he") ? "he-IL" : "en-US";
}

const HEBREW_LETTER = /[֐-׿]/;

/** Hebrew when the text carries a Hebrew letter, otherwise the fallback. */
export function detectSpeechTag(
  text: string,
  fallback: string | null | undefined = "en",
): SpeechTag {
  return HEBREW_LETTER.test(text) ? "he-IL" : speechTag(fallback);
}

const norm = (lang: string) => lang.toLowerCase().replace("_", "-");

/** An exact-tag voice first, then any voice of the same language; else null. */
export function pickVoice<V extends VoiceLike>(
  voices: readonly V[],
  tag: SpeechTag,
): V | null {
  const wanted = norm(tag);
  return (
    voices.find((voice) => norm(voice.lang) === wanted) ??
    voices.find(
      (voice) => norm(voice.lang).split("-")[0] === wanted.split("-")[0],
    ) ??
    null
  );
}

/** The longest text the tenant voice takes (the service refuses more). */
export const TENANT_TTS_MAX_CHARS = 1000;

/** `text` trimmed and cut for the tenant voice: at a sentence end when one fits, else hard. */
export function clipForTenantVoice(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= TENANT_TTS_MAX_CHARS) {
    return trimmed;
  }
  const head = trimmed.slice(0, TENANT_TTS_MAX_CHARS);
  const end = Math.max(
    ...[".", "!", "?", "。", "؟", "\n"].map((mark) => head.lastIndexOf(mark)),
  );
  return (end > 0 ? head.slice(0, end + 1) : head).trim();
}

export function missingVoiceNote(tag: SpeechTag): string {
  return `No ${tag === "he-IL" ? "Hebrew" : "English"} voice is installed; using the default voice.`;
}

/** Cancels anything being spoken. Safe where speech is unavailable. */
export function stopSpeaking(win?: unknown): void {
  try {
    synthesisOf(win)?.cancel();
  } catch {
    // Nothing is speaking, or the engine is gone.
  }
}

/**
 * Speaks `text` in the voice that matches `tag`. Returns whether a matching
 * voice was used, or null when speech is unavailable. `onDone` fires when the
 * line ends, fails or is cancelled.
 */
export function speakText(
  text: string,
  tag: SpeechTag,
  onDone: () => void,
  win?: unknown,
): { matched: boolean } | null {
  const synth = synthesisOf(win);
  const Utterance = speechWindow(win)?.SpeechSynthesisUtterance;
  if (synth === null || Utterance === undefined || text.trim() === "") {
    return null;
  }
  try {
    synth.cancel();
    const utterance = new Utterance(text);
    const voice = pickVoice(synth.getVoices(), tag);
    utterance.lang = voice?.lang ?? tag;
    utterance.voice = voice;
    utterance.onend = onDone;
    utterance.onerror = onDone;
    synth.speak(utterance as never);
    return { matched: voice !== null };
  } catch {
    onDone();
    return null;
  }
}

/** The whole transcript so far (final and interim) from one recognition event. */
export function transcriptOf(event: RecognitionEventLike): string {
  let out = "";
  for (let i = 0; i < event.results.length; i += 1) {
    out += event.results[i]![0].transcript;
  }
  return out.trim();
}

/** Joins what was already typed with the dictated words. */
export function joinDictation(base: string, spoken: string): string {
  if (spoken === "") {
    return base;
  }
  return base === "" || /\s$/.test(base) ? base + spoken : `${base} ${spoken}`;
}

export const SHOW_TEXT_KEY = "lab.showText";
export const READ_ALOUD_KEY = "lab.readAloud";

/** A remembered on/off choice; storage may be absent or throw. */
export function readFlag(key: string, fallback: boolean): boolean {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value === "1";
  } catch {
    return fallback;
  }
}

export function writeFlag(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Private window or blocked storage: the choice just is not remembered.
  }
}
